import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { img } from "../../lib/utils-app";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ImageUploader } from "../../components/ImageUploader";

const empty = { name: "", title: "", phone: "", whatsapp: "", email: "", photo: "" };

export function AgentsManager() {
  const { lang } = useLang();
  const es = lang === "es";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => { setLoading(true); const { data } = await api.get("/agents"); setItems(data.items); setLoading(false); };
  useEffect(() => { load(); }, []);
  const remove = async (a) => { if (!window.confirm(es ? `¿Eliminar ${a.name}?` : `Delete ${a.name}?`)) return; await api.delete(`/agents/${a.id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-4xl font-light">{es ? "Agentes" : "Agents"}</h1><p className="text-muted-foreground mt-1">{items.length}</p></div>
        <button onClick={() => { setEditing({ ...empty }); setOpen(true); }} className="flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground label-caps hover:brightness-110" data-testid="add-agent-btn"><Plus size={16} /> {es ? "Nuevo agente" : "New agent"}</button>
      </div>
      {loading ? <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="agents-grid">
          {items.map((a) => (
            <div key={a.id} className="border border-border p-4 flex gap-3 items-center" data-testid={`agent-card-${a.id}`}>
              <img src={img(a.photo)} alt="" className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0"><p className="font-medium truncate">{a.name}</p><p className="text-xs text-muted-foreground truncate">{a.title || a.email}</p></div>
              <button onClick={() => { setEditing({ ...empty, ...a }); setOpen(true); }} className="p-2 text-muted-foreground hover:text-primary" data-testid={`edit-agent-${a.id}`}><Pencil size={15} /></button>
              <button onClick={() => remove(a)} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`delete-agent-${a.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
      <AgentForm open={open} setOpen={setOpen} editing={editing} onSaved={load} lang={lang} />
    </div>
  );
}

function AgentForm({ open, setOpen, editing, onSaved, lang }) {
  const es = lang === "es";
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  if (!editing) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { if (form.id) await api.put(`/agents/${form.id}`, form); else await api.post("/agents", form); toast.success(es ? "Guardado" : "Saved"); setOpen(false); onSaved(); }
    catch { toast.error("Error"); } finally { setSaving(false); }
  };
  const inp = "w-full mt-1.5 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none";
  const lbl = "label-caps text-muted-foreground text-[0.6rem]";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{form.id ? (es ? "Editar agente" : "Edit agent") : (es ? "Nuevo agente" : "New agent")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="agent-form">
          <div><label className={lbl}>{es ? "Foto" : "Photo"}</label><div className="mt-1.5"><ImageUploader single aspect={1} value={form.photo ? [form.photo] : []} onChange={(a) => set("photo", a[0] || "")} lang={lang} /></div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>{es ? "Nombre" : "Name"}</label><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="agent-name" /></div>
            <div><label className={lbl}>{es ? "Título" : "Title"}</label><input value={form.title} onChange={(e) => set("title", e.target.value)} className={inp} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>{es ? "Teléfono" : "Phone"}</label><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>WhatsApp</label><input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+1 305 555 0100" className={inp} data-testid="agent-whatsapp" /></div>
          </div>
          <div><label className={lbl}>Email</label><input value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} /></div>
          <button disabled={saving} className="w-full py-3 bg-primary text-primary-foreground label-caps disabled:opacity-50" data-testid="agent-save">{saving ? "…" : (es ? "Guardar" : "Save")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
